import { observer } from 'mobx-react-lite';
import {
  Box, Card,
  CardHeader, CssBaseline, Divider, Grid, IconButton, Paper, Stack, Tooltip, Typography, useMediaQuery
} from '@mui/material';
import { CCPalletInfo, FullCustomerCollection } from '../../../app/models/customer';
import { format } from 'date-fns';
import React, { useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import UndoIcon from '@mui/icons-material/Undo';
import { mainStyles } from '../../mainStyles';
import CcProductInfoForm from '../basicUserForms/CcProductInfoForm';
import { useStore } from '../../../app/stores/store';
import KoUpdateForm from '../../admin/reports/collections/Forms/KoUpdateForm';
import { useEffectOnce } from 'usehooks-ts';
import { useParams } from 'react-router-dom';
import agent from '../../../app/api/agent';
import { withNavbar } from '../../../app/layout/HOC';
import { router } from '../../../app/router/Routes';
import SkeltonAnimation from '../../../app/layout/SkeltonAnimation';

const gridContainer = {
  xs: 1,
  sm: 2,
  md: 3
} as const;

const getHeader = (label: string, isMobile: boolean) => {
  return (
    <Typography variant='h4' sx={{ fontSize: isMobile ? 12 : 16, flexShrink: 0, py: 2, ml: 1 }}>
      <strong>{label}</strong>
    </Typography>
  )
}
const textGen = (title: string, value: string | number, xs: number, md: number) => {
  return <Grid item xs={xs} md={md} key={title}>
    <Stack sx={{ pl: 2 }} direction='row' spacing={1} alignItems={'flex-start'} justifyContent='flex-start'>
      <Box component='div' sx={{ width: 200 }}>
        <Typography variant='body1'>
          <strong>{title}</strong>
        </Typography>
      </Box>
      <Typography variant='body2' sx={{ color: 'yellow' }}>
        <strong>{value || "---"}</strong>
      </Typography>
    </Stack>
  </Grid>
}

const palletGen = (pallet: CCPalletInfo, xs: number, md: number) => {
  return <Grid item xs={xs} md={md} key={pallet.id}>
    <Typography variant='body2'>
      <strong>Type:&nbsp;</strong>{pallet.palletType}
    </Typography>
    <Typography variant='body2'>
      <strong>Width:&nbsp;</strong>{pallet.width}&nbsp;x&nbsp;<strong>Length:&nbsp;</strong>{pallet.length}
    </Typography>
    <Typography variant='body2'>
      <strong>Height:&nbsp;</strong>{pallet.height}&nbsp;x&nbsp;<strong>Weight:&nbsp;</strong>{pallet.weight}
    </Typography>
    <Typography variant='body2'>
      <strong>Width:&nbsp;</strong>{pallet.quantity}
    </Typography>
    <Typography variant='body2'>
      <strong>Information:&nbsp;</strong>{pallet.information}
    </Typography>
    <Divider />
  </Grid>
}

function CollectionReportDetail() {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<FullCustomerCollection | null>(null)
  const [openEditForm, setOpenEditForm] = React.useState(false);
  const showEditbtn = true;

  const { id } = useParams<{ id: string }>();

  useEffectOnce(() => {
    const fetchCollection = async (ccId: string) => {
      setLoading(true);
      agent.Customer.getCustomerCollectionById(ccId)
        .then(collection => setReport(collection))
        .catch(error => console.log(error))
        .finally(() => setLoading(false));
    }
    if (id) {
      fetchCollection(id);
    }
  });

  const isMobile = !useMediaQuery('(min-width:900px)');

  const { adminCollectionStore: {
    addKoNumberToCollection },
    userStore: { isT1aAdmin },
    modalStore: { openModal, closeModal }
  } = useStore();

  if (!report) return null;
  const { contactInfo, products, pallets } = report;

  const koUpdateHandler = async (id: string, koNumber: string) => {
    if (id) {

      var res = await addKoNumberToCollection(id, koNumber);
      if (res && report) {
        setReport({
          ...report,
          koNumber: koNumber
        });
        closeModal();
      }

    }
  }

  const handleOpenEditForm = () => {
    setOpenEditForm(true);
  };
  const handleCloseEditForm = () => {
    setOpenEditForm(false);
    // resetSelectedCollectionReport();
  };

  if (loading) return loading &&
    <Box component='div' sx={{ mt: 12 }}>
      {loading && <SkeltonAnimation iteration={10} />}
    </Box>

  return (
    <Box component='div' >
      <CssBaseline />
      <Grid sx={{ mt: 4, pb: 4 }} container spacing={2} direction="column" justifyContent="center" alignItems="center">
        {!openEditForm &&
          <Grid item xs={12} key={report.id} >
            <Card sx={mainStyles.ccCardStyles}>
              <CardHeader
                action={
                  <>
                    <Tooltip title="Back" placement="left">
                      <IconButton onClick={() => router.navigate(-1)}>
                        <UndoIcon color='info' />
                      </IconButton>
                    </Tooltip>
                    {((showEditbtn && !report.koNumber) || isT1aAdmin) &&
                      <Tooltip title="Edit">
                        <IconButton aria-label="edit" onClick={handleOpenEditForm}>
                          <EditIcon color='info' />
                        </IconButton>
                      </Tooltip>
                    }
                  </>
                }
                title={<Typography
                  component={isT1aAdmin ? 'button' : 'span'}
                  onClick={() => isT1aAdmin ? openModal(<KoUpdateForm id={report.id} koNum={report.koNumber}
                    koUpdateHandler={koUpdateHandler} />)
                    : null}
                  variant='h6'
                  sx={{
                    cursor: isT1aAdmin ? 'pointer' : 'default',
                    '&:hover': {
                      opacity: [0.9, 0.8, 0.7],
                    },
                  }}>
                  {report.koNumber ? report.koNumber : 'Waiting for approval'} </Typography>}
                subheader={format(new Date(report.createdAt), 'dd MMM yyyy')}
              />
              <Paper sx={{
                bgcolor: '#003755',
                '& .MuiAccordionSummary-content': {
                  m: '0 auto'
                },
                '& .MuiButtonBase-root': {
                  height: 36
                },

              }}>
                <Grid container rowSpacing={1} columnSpacing={gridContainer}>
                  <Grid item xs={12} >
                    <Typography variant='h6'>{getHeader('General Info', isMobile)}</Typography>
                    <Grid container direction='row' wrap='wrap' rowSpacing={1} columnSpacing={gridContainer}
                    >
                      {textGen('Reference Number', report.cgiRefNumber ? report.cgiRefNumber : "", 12, 12)}
                      {textGen('Email', contactInfo!.email, 12, 12)}
                      {textGen('Company Name', contactInfo.companyName, 12, 12)}
                      {textGen('Address', contactInfo.address, 12, 12)}
                      {textGen('Address2', contactInfo.address1, 12, 12)}
                      {textGen('PostCode', contactInfo.postCode, 12, 12)}
                      {textGen('City', contactInfo.city, 12, 12)}
                      {textGen('Country', contactInfo.country, 12, 12)}
                      {textGen('Contact Name', contactInfo.localContactName, 12, 12)}
                      {textGen('Tel. Number', contactInfo.telephoneNumber, 12, 12)}
                      {textGen('Mob. Number', contactInfo.mobileNumber, 12, 12)}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container rowSpacing={1} columnSpacing={gridContainer}>
                  <Grid item xs={12}>
                    <Typography variant='h6'>{getHeader('Collection Info', isMobile)}</Typography>
                    <Grid container direction='row' wrap='wrap' rowSpacing={1} columnSpacing={gridContainer}>
                      {report.isDone &&
                        textGen('Collection Date', format(new Date(report.collectionDate!), 'dd MMM yyyy'), 12, 12)}

                      {textGen('Time', `From: ${report.collectionHourFrom} To: ${report.collectionHourTo}`, 12, 12)}
                      {textGen('Collect From / Deliver to', report.collectionFrom, 12, 12)}
                      {textGen('Addition information regrading delivery/collection', report.collectionNote, 12, 12)}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container rowSpacing={1} columnSpacing={gridContainer}>
                  <Grid item xs={12}>
                    <Typography variant='h6'>{getHeader('Bios-Hdd Passwords', isMobile)}</Typography>
                    <Grid container direction='row' wrap='wrap' rowSpacing={1} columnSpacing={gridContainer}>
                      {textGen('Do you have Passwords', report.havePassword ? 'YES' : 'NO', 12, 12)}
                      {textGen('Do you have Password file', report.haveFilePassword ? 'YES' : 'NO', 12, 12)}
                      {textGen('Do you have BIOS Password', report.isBiosPassword ? 'YES' : 'NO', 12, 12)}
                      {textGen('BIOS', report.isBiosPassword ? report.biosPassword : '', 12, 12)}
                      {textGen('Do you have HDD Password', report.isHddPassword ? 'YES' : 'NO', 12, 12)}
                      {textGen('HDD', report.isHddPassword ? report.hddPassword : '', 12, 12)}
                      {textGen('Do you have Master Password', report.isMasterPassword ? 'YES' : 'NO', 12, 12)}
                      {textGen('Master password', report.isMasterPassword ? report.masterPassword : '', 12, 12)}
                      {textGen('Do you have MDM Comp. Password', report.isMdmComputracePassword ? 'YES' : 'NO', 12, 12)}
                      {textGen('MDM Comp. Password', report.isMdmComputracePassword ? report.mdmComputracePassword : '', 12, 12)}
                      {textGen('Do you have Autopilot Password', report.isAutopilotPassword ? 'YES' : 'NO', 12, 12)}
                      {textGen('Autopilot Password', report.isAutopilotPassword ? report.autopilotPassword : '', 12, 12)}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container rowSpacing={1} columnSpacing={gridContainer}>
                  <Grid item xs={12}>
                    <Typography variant='h6'>{getHeader('Product Info', isMobile)}</Typography>
                    <Grid container direction='row' wrap='wrap' rowSpacing={1} columnSpacing={gridContainer}>
                      {products.map((prod) => (
                        textGen(prod.productType.toUpperCase(), prod.quantity + ' Info:' + prod.information.toUpperCase(), 12, 12)
                      ))}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container rowSpacing={1} columnSpacing={gridContainer}>
                  <Grid item xs={12}>
                    <Typography variant='h6'>{getHeader('Box-Cages', isMobile)}</Typography>
                    <Grid container direction='row' wrap='wrap' rowSpacing={1} columnSpacing={gridContainer}>
                      {textGen('T1A Estimate', report.t1aEstimate ? 'YES' : 'NO', 12, 12)}
                      {textGen('secure Transport', report.secureTransport ? 'YES' : 'NO', 12, 12)}
                      {/* {textGen('Single Box (Carton box for return shipment with UPS)', report.singleBoxQty, 12, 12)} */}
                      {report.singleBox &&
                        <Grid item xs={12} md={12}>
                          <Box sx={{ pl: 2 }}>
                            <Typography variant='body2' sx={{ pb: 0, mb: 0 }}>
                              <strong>Single Box (Carton box for return shipment with UPS)</strong><br />
                              <strong>&nbsp;&nbsp;Qty. :&nbsp;</strong>{report.singleBoxQty} <br />
                            </Typography>
                            <Typography variant='body2' sx={{ pb: 0, mb: 0, ml: 2, mt: 1 }}>
                              <span style={{ fontSize: 11, fontStyle: 'italic' }}><sup>*</sup>T1A will estimate the size and quantity of boxes depending on the details provided in the Product Info</span>
                            </Typography>
                          </Box>
                        </Grid>
                      }
                      {report.securityCageBox &&
                        <Grid item xs={12} md={12}>
                          <Box sx={{ pl: 2 }}>
                            <Typography variant='body2'>
                              <strong>Security Cage (120*80*127 cm/max 500 kg load):&nbsp;</strong>{`${report.securityCageBoxOption1 ? 'YES' : 'NO'}`}
                              <br />
                              <strong>&nbsp;&nbsp;Qty. :&nbsp;</strong>{report.securityCageBoxQty1} <br />
                            </Typography>
                          </Box>
                        </Grid>
                      }
                      {report.securityCageBox &&
                        <Grid item xs={12} md={12}>
                          <Box sx={{ pl: 2 }}>
                            <Typography variant='body2'>
                              <strong>Security Mini-Cage (80*72*110 cm/max 300 kg load):&nbsp;</strong>{`${report.securityCageBoxOption2 ? 'YES' : 'NO'}`}
                              <br />
                              <strong>&nbsp;&nbsp;Qty. :&nbsp;</strong>{report.securityCageBoxQty2} <br />
                            </Typography>
                          </Box>
                        </Grid>
                      }
                      {report.securityCageBox &&
                        <Grid item xs={12} md={12}>
                          <Box sx={{ pl: 2 }}>
                            <Typography variant='body2'>
                              <strong>Security Box (120*80*110 cm/max 450 kg load):&nbsp;</strong>{`${report.securityCageBoxOption3 ? 'YES' : 'NO'}`}
                              <br />
                              <strong>&nbsp;&nbsp;Qty. :&nbsp;</strong>{report.securityCageBoxQty3} <br />
                            </Typography>
                          </Box>
                        </Grid>
                      }

                      {report.otherPrepackedPalletsOrSimilar &&
                        < Grid item xs={12} md={12}>
                          <Box sx={{ pl: 2 }}>
                            <Typography variant='body2'>
                              <strong>Other/Pre-packed pallets :&nbsp;</strong><br />
                              <strong>&nbsp;&nbsp;Info. :&nbsp;</strong>{report.otherPrepackedPalletsOrSimilarFreetext}
                            </Typography>
                            <Grid container direction='row' wrap='wrap' rowSpacing={1} columnSpacing={gridContainer}>
                              {pallets.map((pallet) => (
                                palletGen(pallet, 12, 12)
                              ))}
                            </Grid>
                          </Box>
                        </Grid>
                      }
                      {textGen('On Site Packing', report.onSitePacking ? 'YES' : 'NO', 12, 12)}
                    </Grid>
                  </Grid>
                </Grid>

              </Paper>
            </Card>
          </Grid>
        }
      </Grid>
      {openEditForm && report &&
        <Box>
          <CcProductInfoForm prodInfo={report} handleCloseEditForm={handleCloseEditForm} />
        </Box>
      }
    </Box >

  );
}

const CollectionReportDetailPage = withNavbar(observer(CollectionReportDetail));
export default CollectionReportDetailPage;